import { 
  FixtureFormData, 
  FixtureDimensions, 
  FixtureValidationSchema,
  FixtureType,
  FixturePaginatedResponse,
  ApiResponse,
  FixtureBatchResult
} from '../types/fixture';
import { createSuccessResponse, createErrorResponse, createPaginatedResponse } from './responseTransformer';

// Validation schema with business rules
export const fixtureValidationSchema: FixtureValidationSchema = {
  typeName: {
    required: true,
    minLength: 2,
    maxLength: 100
  },
  typeCode: {
    required: true,
    pattern: /^[A-Z0-9_-]+$/,
    maxLength: 20,
    unique: true
  },
  dimensions: {
    width: { required: true, min: 0.1 },
    height: { required: true, min: 0.1 },
    depth: { required: true, min: 0.1 }
  }
};

// Validation error interface
export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Validate fixture type name
export const validateTypeName = (typeName: string): ValidationError[] => {
  const errors: ValidationError[] = [];
  const schema = fixtureValidationSchema.typeName;

  if (!typeName || typeName.trim().length === 0) {
    errors.push({
      field: 'typeName',
      message: 'Fixture type name is required',
      code: 'REQUIRED'
    });
  } else {
    const trimmedName = typeName.trim();
    if (trimmedName.length < schema.minLength) {
      errors.push({
        field: 'typeName',
        message: `Fixture type name must be at least ${schema.minLength} characters`,
        code: 'MIN_LENGTH'
      });
    }
    if (trimmedName.length > schema.maxLength) {
      errors.push({
        field: 'typeName',
        message: `Fixture type name must not exceed ${schema.maxLength} characters`,
        code: 'MAX_LENGTH'
      });
    }
  }

  return errors;
};

// Validate fixture type code
export const validateTypeCode = (typeCode: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!typeCode || typeCode.trim().length === 0) {
    errors.push({
      field: 'typeCode',
      message: 'Fixture type code is required',
      code: 'REQUIRED'
    });
  }

  return errors;
};

// Validate fixture dimensions
export const validateDimensions = (dimensions: FixtureDimensions): ValidationError[] => {
  const errors: ValidationError[] = [];
  const schema = fixtureValidationSchema.dimensions;

  // Validate width
  if (dimensions.width === undefined || dimensions.width === null) {
    errors.push({
      field: 'dimensions.width',
      message: 'Width is required',
      code: 'REQUIRED'
    });
  } else if (dimensions.width < schema.width.min) {
    errors.push({
      field: 'dimensions.width',
      message: `Width must be at least ${schema.width.min}`,
      code: 'MIN_VALUE'
    });
  }

  // Validate height
  if (dimensions.height === undefined || dimensions.height === null) {
    errors.push({
      field: 'dimensions.height',
      message: 'Height is required',
      code: 'REQUIRED'
    });
  } else if (dimensions.height < schema.height.min) {
    errors.push({
      field: 'dimensions.height',
      message: `Height must be at least ${schema.height.min}`,
      code: 'MIN_VALUE'
    });
  }

  // Validate depth
  if (dimensions.depth === undefined || dimensions.depth === null) {
    errors.push({
      field: 'dimensions.depth',
      message: 'Depth is required',
      code: 'REQUIRED'
    });
  } else if (dimensions.depth < schema.depth.min) {
    errors.push({
      field: 'dimensions.depth',
      message: `Depth must be at least ${schema.depth.min}`,
      code: 'MIN_VALUE'
    });
  }

  // Validate unit
  const validUnits = ['mm', 'cm', 'inch'];
  if (!validUnits.includes(dimensions.unit)) {
    errors.push({
      field: 'dimensions.unit',
      message: 'Invalid unit. Must be mm, cm, or inch',
      code: 'INVALID_VALUE'
    });
  }

  return errors;
};

// Validate hole count
export const validateHoleCount = (holeCount: number): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Validate hole count
  if (holeCount === undefined || holeCount === null) {
    errors.push({
      field: 'holeCount',
      message: 'Hole count is required',
      code: 'REQUIRED'
    });
  } else if (holeCount < 0) {
    errors.push({
      field: 'holeCount',
      message: 'Hole count cannot be negative',
      code: 'MIN_VALUE'
    });
  } else if (!Number.isInteger(holeCount)) {
    errors.push({
      field: 'holeCount',
      message: 'Hole count must be a whole number',
      code: 'INVALID_VALUE'
    });
  }

  return errors;
};

// Validate cross beam count
export const validateCrossBeamCount = (crossBeamCount: number): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Validate cross beam count
  if (crossBeamCount === undefined || crossBeamCount === null) {
    errors.push({
      field: 'crossBeamCount',
      message: 'Cross beam count is required',
      code: 'REQUIRED'
    });
  } else if (crossBeamCount < 1) {
    errors.push({
      field: 'crossBeamCount',
      message: 'Cross beam count must be at least 1',
      code: 'MIN_VALUE'
    });
  } else if (!Number.isInteger(crossBeamCount)) {
    errors.push({
      field: 'crossBeamCount',
      message: 'Cross beam count must be a whole number',
      code: 'INVALID_VALUE'
    });
  }

  return errors;
};

// Validate image file
export const validateImageFile = (file?: File): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (file) {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      errors.push({
        field: 'image',
        message: 'Image must be JPEG, PNG, or GIF format',
        code: 'INVALID_FILE_TYPE'
      });
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      errors.push({
        field: 'image',
        message: 'Image size must not exceed 5MB',
        code: 'FILE_TOO_LARGE'
      });
    }
  }

  return errors;
};

// Comprehensive fixture form validation
export const validateFixtureForm = (formData: FixtureFormData): ValidationResult => {
  const allErrors: ValidationError[] = [];

  // Validate all fields
  allErrors.push(...validateTypeName(formData.typeName));
  allErrors.push(...validateTypeCode(formData.typeCode));
  allErrors.push(...validateDimensions(formData.dimensions));
  allErrors.push(...validateHoleCount(formData.holeCount));
  allErrors.push(...validateCrossBeamCount(formData.crossBeamCount));
  allErrors.push(...validateImageFile(formData.image));

  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
};

// Validate individual field for real-time validation
export const validateField = (fieldName: string, value: any, formData?: Partial<FixtureFormData>): ValidationError[] => {
  switch (fieldName) {
    case 'typeName':
      return validateTypeName(value);
    case 'typeCode':
      return validateTypeCode(value);
    case 'dimensions':
      return validateDimensions(value);
    case 'holeCount':
      return validateHoleCount(value);
    case 'crossBeamCount':
      return validateCrossBeamCount(value);
    case 'image':
      return validateImageFile(value);
    default:
      return [];
  }
};

// Helper function to get error message for a specific field
export const getFieldError = (fieldName: string, errors: ValidationError[]): string | undefined => {
  const fieldError = errors.find(error => error.field === fieldName);
  return fieldError?.message;
};

// Helper function to check if a field has errors
export const hasFieldError = (fieldName: string, errors: ValidationError[]): boolean => {
  return errors.some(error => error.field === fieldName);
};

// Format validation errors for display
export const formatValidationErrors = (errors: ValidationError[]): Record<string, string> => {
  const formattedErrors: Record<string, string> = {};
  errors.forEach(error => {
    formattedErrors[error.field] = error.message;
  });
  return formattedErrors;
};

// API Response wrapper utilities for fixture operations

/**
 * Creates a success response for fixture operations
 * @param data The fixture data
 * @returns Formatted API response
 */
export const createFixtureSuccessResponse = <T>(data: T): ApiResponse<T> => {
  return createSuccessResponse(data);
};

/**
 * Creates an error response for fixture operations
 * @param message Error message
 * @param code Error code
 * @returns Formatted API error response
 */
export const createFixtureErrorResponse = <T>(message: string, code?: string): ApiResponse<T> => {
  return createErrorResponse(message, code);
};

/**
 * Creates a paginated response for fixture list operations
 * @param fixtures Array of fixtures
 * @param page Current page
 * @param pageSize Items per page
 * @param total Total count
 * @returns Formatted paginated response
 */
export const createFixturePaginatedResponse = (
  fixtures: FixtureType[],
  page: number,
  pageSize: number,
  total: number
): ApiResponse<FixturePaginatedResponse<FixtureType>> => {
  return createPaginatedResponse(fixtures, page, pageSize, total);
};

/**
 * Creates a batch operation result response
 * @param successCount Number of successful operations
 * @param failures Array of failed operations with reasons
 * @returns Batch operation result
 */
export const createBatchOperationResult = (
  successCount: number,
  failures: { id: string; reason: string }[]
): FixtureBatchResult => {
  return {
    successCount,
    failureCount: failures.length,
    failures
  };
};

/**
 * Validates fixture data integrity for database operations
 * @param fixture The fixture to validate
 * @returns Validation result with any integrity issues
 */
export const validateFixtureDataIntegrity = (fixture: FixtureType): ValidationResult => {
  const errors: ValidationError[] = [];

  // Check required fields
  if (!fixture.id) {
    errors.push({
      field: 'id',
      message: 'Fixture ID is required',
      code: 'REQUIRED'
    });
  }

  if (!fixture.gmtCreate) {
    errors.push({
      field: 'gmtCreate',
      message: 'Created date is required',
      code: 'REQUIRED'
    });
  }

  if (!fixture.gmtModified) {
    errors.push({
      field: 'gmtModified',
      message: 'Updated date is required',
      code: 'REQUIRED'
    });
  }

  if (!fixture.creatorName) {
    errors.push({
      field: 'creatorName',
      message: 'Created by user is required',
      code: 'REQUIRED'
    });
  }

  if (!fixture.modifierName) {
    errors.push({
      field: 'modifierName',
      message: 'Updated by user is required',
      code: 'REQUIRED'
    });
  }

  // Validate status
  const validStatuses = ['Draft', 'Active', 'Inactive'];
  if (!validStatuses.includes(fixture.status)) {
    errors.push({
      field: 'status',
      message: 'Invalid fixture status',
      code: 'INVALID_VALUE'
    });
  }

  // Validate core form data
  const formValidation = validateFixtureForm({
    typeName: fixture.typeName,
    typeCode: fixture.typeCode,
    dimensions: fixture.dimensions,
    holeCount: fixture.holeCount,
    crossBeamCount: fixture.crossBeamCount,
    description: fixture.description
  });

  errors.push(...formValidation.errors);

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Advanced validation functions for business rules

/**
 * Validates fixture type code uniqueness
 * @param typeCode The type code to validate
 * @param existingFixtures Array of existing fixtures to check against
 * @param excludeId Optional ID to exclude from uniqueness check (for updates)
 * @returns Validation errors if type code is not unique
 */
export const validateTypeCodeUniqueness = (
  typeCode: string,
  existingFixtures: FixtureType[],
  excludeId?: string
): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  if (!typeCode) {
    return errors; // Basic validation will catch this
  }

  const trimmedCode = typeCode.trim().toUpperCase();
  const duplicateFixture = existingFixtures.find(fixture => 
    fixture.typeCode.toUpperCase() === trimmedCode && fixture.id !== excludeId
  );

  if (duplicateFixture) {
    errors.push({
      field: 'typeCode',
      message: `Type code "${typeCode}" is already in use by fixture "${duplicateFixture.typeName}"`,
      code: 'DUPLICATE_TYPE_CODE'
    });
  }

  return errors;
};

/**
 * Validates fixture status transitions
 * @param currentStatus Current fixture status
 * @param newStatus Desired new status
 * @returns Validation errors if transition is not allowed
 */
export const validateStatusTransition = (
  currentStatus: string,
  newStatus: string
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Define allowed status transitions
  const allowedTransitions: Record<string, string[]> = {
    'Draft': ['Active'],
    'Active': ['Inactive'],
    'Inactive': ['Active']
  };

  if (currentStatus === newStatus) {
    return errors; // No transition needed
  }

  const allowedNextStatuses = allowedTransitions[currentStatus] || [];
  if (!allowedNextStatuses.includes(newStatus)) {
    errors.push({
      field: 'status',
      message: `Cannot change status from "${currentStatus}" to "${newStatus}". Allowed transitions: ${allowedNextStatuses.join(', ')}`,
      code: 'INVALID_STATUS_TRANSITION'
    });
  }

  return errors;
};

/**
 * Validates batch operation constraints
 * @param fixtures Array of fixtures to operate on
 * @param operation The batch operation to perform
 * @returns Validation errors for fixtures that cannot be operated on
 */
export const validateBatchOperation = (
  fixtures: FixtureType[],
  operation: 'activate' | 'deactivate'
): ValidationError[] => {
  const errors: ValidationError[] = [];

  fixtures.forEach((fixture, index) => {
    if (operation === 'activate' && fixture.status !== 'Draft') {
      errors.push({
        field: `fixtures[${index}]`,
        message: `Fixture "${fixture.typeName}" cannot be activated. Only Draft fixtures can be activated.`,
        code: 'INVALID_BATCH_OPERATION'
      });
    }

    if (operation === 'deactivate' && fixture.status !== 'Active') {
      errors.push({
        field: `fixtures[${index}]`,
        message: `Fixture "${fixture.typeName}" cannot be deactivated. Only Active fixtures can be deactivated.`,
        code: 'INVALID_BATCH_OPERATION'
      });
    }
  });

  return errors;
};

/**
 * Validates dimension consistency and business rules
 * @param dimensions The dimensions to validate
 * @returns Validation errors for dimension business rules
 */
export const validateDimensionBusinessRules = (dimensions: FixtureDimensions): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Business rule: Width should not be more than 10 times the depth
  if (dimensions.width > 0 && dimensions.depth > 0) {
    const ratio = dimensions.width / dimensions.depth;
    if (ratio > 10) {
      errors.push({
        field: 'dimensions',
        message: 'Width to depth ratio should not exceed 10:1 for structural stability',
        code: 'DIMENSION_RATIO_EXCEEDED'
      });
    }
  }

  // Business rule: Height should be reasonable for shelf fixtures
  if (dimensions.height > 0) {
    const maxHeight = dimensions.unit === 'mm' ? 3000 : dimensions.unit === 'cm' ? 300 : 118; // ~3m max
    if (dimensions.height > maxHeight) {
      errors.push({
        field: 'dimensions.height',
        message: `Height exceeds maximum allowed value of ${maxHeight}${dimensions.unit}`,
        code: 'HEIGHT_EXCEEDED'
      });
    }
  }

  return errors;
};

/**
 * Comprehensive fixture form validation with business rules
 * @param formData The form data to validate
 * @param existingFixtures Array of existing fixtures for uniqueness check
 * @param excludeId Optional ID to exclude from uniqueness check
 * @returns Complete validation result
 */
export const validateFixtureFormWithBusinessRules = (
  formData: FixtureFormData,
  existingFixtures: FixtureType[] = [],
  excludeId?: string
): ValidationResult => {
  const allErrors: ValidationError[] = [];

  // Basic validation
  const basicValidation = validateFixtureForm(formData);
  allErrors.push(...basicValidation.errors);

  // Business rule validations
  allErrors.push(...validateTypeCodeUniqueness(formData.typeCode, existingFixtures, excludeId));
  allErrors.push(...validateDimensionBusinessRules(formData.dimensions));

  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
};